<template lang="pug">
div
  div.pa-4
    v-row.flex-nowrap(no-gutters align="center")
      v-col.grow
        v-tabs(v-model="tabs" hide-slider height="36" @change="")
          v-tab
            div.text-sm.font-medium.normal-case Patient history
          v-tab
            div.text-sm.font-medium.normal-case Treatment plan
      v-col.shrink(v-if="hasReservationsOrPreorders")
        yz-card.d-flex.align-center(style="gap: 12px; background-color: #FFFAE6; border: 0.5px solid var(--v-neutral400); padding: 4px 4px 4px 8px;" radius="10px")
          span.yz-medium-body-sm.text-neutral-700.no-wrap Reservation & pre-order: {{ reservationPreorderCount }}
          yz-btn(label="View" size="sm" background-color="white" outlined, @click="onClickViewReservations")
      v-col.shrink
        y-button(v-if="showSwitchOldVersion" label="Switch old version" outlined color="neutral300" text-color="neutral700" background-color="var(--v-neutral300)" append-icon="$yzi-repeat-01" icon-size="15" icon-color="neutral700" size="xxs" height="24" @click="showAlert = true")
  v-divider
  div.parent-scroll(:style="{ '--right-panel-height': rightPanelHeight }")
    div.child-scroll(:style="{ '--right-panel-height': rightPanelHeight, '--nve-right-margin': nveRightMargin }")
      keep-alive
        patient-history(v-if="!tabs")
        treatment-plan(v-else ref="treatmentPlan")

  .dialogs
    view-reservations-preorders-dialog(v-if="showReservationPreorderDialog" :open.sync="showReservationPreorderDialog" :reservations="reservations" :preorders="preorders" @confirm="onConfirmReservationSelection")
    y-dialog(v-if="showAlert" v-model="showAlert" :max-width="600" persistent content-class="round-2xl")
      v-card
        v-card-title.pa-4
          v-row(no-gutters align="center")
            v-col(cols="10" offset="1")
              div.text-lg.font-semibold.text-center.neutral700--text Switch back
            v-col(cols="1" align="right")
              v-btn(icon @click="showAlert = false" small color="primary")
                v-icon mdi-close
        v-divider
        v-card-text.pa-6
          div.text-md.font-semibold.neutral700--text You’re about to switch into old version of Consultation page. The old version will be retired in a few months.
          div.text-sm.neutral700--text.mt-4 Click “Confirm” to continue
        v-divider
        v-card-actions.pb-4.pt-3.px-6
          v-spacer
          y-button.mr-3(:label="$t('cancel')" text color="neutral700" size="sm" button-class="px-0" @click="showAlert = false")
          y-button(label="Confirm" color="neutral700" size="sm" button-class="px-3" dark @click="switchOldVersion")
      
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import PatientHistory from '@/components/clinic/consultation/PatientHistory.vue'
import TreatmentPlan from '@/components/clinic/consultation/TreatmentPlan.vue'
import ViewReservationsPreordersDialog from '@/components/clinic/registration/ViewReservationsPreordersDialog.vue'

export default {
  components: { PatientHistory, TreatmentPlan, ViewReservationsPreordersDialog },
  data () {
    return {
      isFetching: false,
      showAlert: false,
      tabs: 0,
      reservations: [],
      preorders: [],
      showReservationPreorderDialog: false,
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapState('clinic', ['cmsOsV2Enabled']),
    showSwitchOldVersion () {
      if (this.cmsOsV2Enabled) return false
      // Hide for members created strictly after 25 Jun 2026 (i.e. 26 Jun onward)
      const created = this.member && this.member.created
      if (created && DateTime.fromISO(created) >= DateTime.fromISO('2026-06-26')) return false
      return true
    },
    ...mapGetters({
      company: 'getCompany',
      displaySize: 'ui/getDisplaySize',
      patient: 'treatment/getPatient',
      leadPid: 'treatment/getLeadPid',
      scrollbarWidth: 'ui/getScrollbarWidth',
    }),
    hasReservationsOrPreorders () {
      return this.reservations.length > 0 || this.preorders.length > 0
    },
    reservationPreorderCount () {
      return this.reservations.length + this.preorders.length
    },
    screenHeight () {
      return window.innerHeight
    },
    rightPanelHeight () {
      let minusHeight = 306 - 72
      switch (this.displaySize) {
        case 'xx-small':
          return `${(this.screenHeight / 0.67) - minusHeight}px`
        case 'x-small':
          return `${(this.screenHeight / 0.75) - minusHeight}px`
        case 'small':
          return `${(this.screenHeight / 0.8) - minusHeight}px`
        case 'medium':
          return `${(this.screenHeight / 0.9) - minusHeight}px`
        case 'large':
          return `${(this.screenHeight) - minusHeight}px`
        case 'x-large':
          return `${(this.screenHeight / 1.1) - minusHeight}px`
        default:
          return `${(this.screenHeight) - minusHeight}px`
      }
    },
    nveRightMargin () {
      return `${(-10 - this.scrollbarWidth)}px`
    },
  },
  mounted () {
    this.fetchActiveMedication()
    if (this.patient?.id) {
      this.fetchReservationsAndPreorders()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchReservationsAndPreorders () {
      try {
        const [reservationsResp, preordersResp] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params: { patient: this.patient.id, status: 'active', page_size: -1 } }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params: { patient: this.patient.id, status: 'active', page_size: -1 } })
        ])
        if (reservationsResp.data?.results) {
          this.reservations = reservationsResp.data.results.filter(r => r.products.length > 0)
        }
        if (preordersResp.data?.results) {
          this.preorders = preordersResp.data.results.filter(r => r.products.length > 0)
        }
      } catch {
        // silently ignore
      }
    },
    onClickViewReservations () {
      this.showReservationPreorderDialog = true
    },
    async onConfirmReservationSelection (items) {
      const failed = await this.fulfillSelectedReservations(items)
      await this.fetchReservationsAndPreorders()
      if (failed.length > 0) {
        const failedList = failed.map(i => `${i.type} #${i.id}`).join(', ')
        this.showMessage(['error', `Reservation & pre-order items added, but failed to fulfill: ${failedList}`])
      } else {
        this.showMessage(['success', 'Reservation & pre-order items added to treatment plan'])
      }
      if (this.$refs.treatmentPlan) {
        this.$refs.treatmentPlan.updateTreatments()
      }
    },
    async fulfillSelectedReservations (items) {
      const failed = []
      for (const item of items) {
        try {
          if (item.type === 'reservation') {
            await this.$axios.post(`/clinics/${this.company.slug}/reservations/${item.id}/fulfill/`, { registration_pid: this.leadPid })
          } else if (item.type === 'preorder') {
            await this.$axios.post(`/clinics/${this.company.slug}/preorders/${item.id}/fulfill/`, { registration_pid: this.leadPid })
          }
        } catch (err) {
          console.error(err)
          failed.push(item)
        }
      }
      return failed
    },
    fetchActiveMedication () {
      this.isFetching = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(resp => {
          let activeMedications = []
          if (resp.data && resp.data.results) {
            activeMedications = resp.data.results.map(m => { return { id: m.id, m_id: m.medication.id, name: m.medication.name } })
          }
          this.$store.commit('treatment/setActiveMedications', activeMedications)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    switchOldVersion () {
      let params = {
        extra_data: {
          ...this.member.extra_data,
          new_consultation: false
        }
      }
      this.$axios.patch(`/members/${this.member.id}/`, params)
        .then(resp => {
          this.$store.commit('user/setMember', resp.data)
          this.showAlert = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-tab {
  min-width: auto;
  padding: 0 12px;
}
.v-tab--active {
  background-color: var(--v-neutral300);
  border-radius: 10px;
}
.parent-scroll {
  overflow: hidden;
  height: var(--right-panel-height) !important;
}
.child-scroll {
  overflow-y: scroll;
  height: var(--right-panel-height) !important;
  margin-right: var(--nve-right-margin);
  padding-right: 10px;
  // padding-bottom: 8px;
}
</style>